// ============================================================
// claimTypeFilter.ts — the case list's CLAIM TYPE filter (pure;
// claimTypeFilter.test.ts). Kept in the URL as ?claimType=<ClaimType>|none
// (the server's rule: backend lib/caseListClaimType). Its options come from
// GET /api/cases/claim-types: ONLY the types among the cases the list would
// show for the other filters, each with its count, plus "Not set".
// ============================================================
import { CLAIM_TYPE_LABELS, CLAIM_TYPE_SHORT_LABELS, isClaimType, type ClaimType } from './claimTypes'

export const CLAIM_TYPE_PARAM = 'claimType'
export const CLAIM_TYPE_NONE = 'none'
export type ClaimTypeFilterValue = ClaimType | typeof CLAIM_TYPE_NONE
export const isClaimTypeFilterValue = (v: unknown): v is ClaimTypeFilterValue => v === CLAIM_TYPE_NONE || isClaimType(v)

export interface ClaimTypeOptions { types: { claimType: ClaimType; count: number }[]; notSet: number }

export interface ClaimTypeOption { value: '' | ClaimTypeFilterValue; label: string; title?: string }
// The dropdown: "All claim types", the types present (short label + count),
// "Not set (n)" if any. A chosen value that's no longer present (e.g. the
// search narrowed it away) stays listed, with 0, so the select never shows a
// value it doesn't offer.
export const claimTypeFilterOptions = (opts: ClaimTypeOptions | null, selected: ClaimTypeFilterValue | null): ClaimTypeOption[] => {
  const out: ClaimTypeOption[] = [{ value: '', label: 'All claim types' }]
  const types = opts?.types ?? []
  for (const t of types) out.push({ value: t.claimType, label: `${CLAIM_TYPE_SHORT_LABELS[t.claimType]} (${t.count})`, title: CLAIM_TYPE_LABELS[t.claimType] })
  if (selected && selected !== CLAIM_TYPE_NONE && !types.some((t) => t.claimType === selected)) {
    out.push({ value: selected, label: `${CLAIM_TYPE_SHORT_LABELS[selected]} (0)`, title: CLAIM_TYPE_LABELS[selected] })
  }
  const notSet = opts?.notSet ?? 0
  if (notSet > 0 || selected === CLAIM_TYPE_NONE) out.push({ value: CLAIM_TYPE_NONE, label: `Not set (${notSet})` })
  return out
}
// Hidden entirely when no case has a type and none are "Not set" (nothing to
// filter) — unless a filter is on, so it can always be turned off. Not shown
// before the first load.
export const showClaimTypeFilter = (opts: ClaimTypeOptions | null, selected: ClaimTypeFilterValue | null): boolean =>
  !!selected || (!!opts && (opts.types.length > 0 || opts.notSet > 0))
